import React, { Fragment, useMemo, useState } from "react";
import useNotiStack from "../../hooks/useNotiStack";
import useStore from "../../states/store";
import { ACTIVE, DELETED, STATUS_COLORS, USER_STATUS } from "../../enums/statusEnums";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import createUsersQueryOptions from "../../queryOptions/createUsersQueryOptions";
import { deleteAccount } from "../../config/apisauce";
import {
  Box,
  Button,
  Card,
  Divider,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import { GenericAvatar } from "../generic/GenericAvatar";
import { GENERIC_COLUMN_PROPS } from "../../enums/generalEnums";
import { formatDate } from "../../helpers/dateHelper";
import useDebounce from "react-use/esm/useDebounce";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { PAGE_OPTIONS } from "../../enums/dataGridEnums";
import { ProfileDetails } from "../../pages/admin/adminHomePage/profileDetails/ProfileDetails";
import { DeleteAccountDialog } from "../../pages/shared/DeleteAccountDialog";
import { ROLES } from "../../enums/RoleEnum";
import CustomDataGrid, { CustomNoResultsOverlay } from "./CustomDataGrid";
import { DAY_ONLY_FORMAT } from "../../enums/dateEnums";
import SuspendCreatorDialog from "../Dialog/SuspendCreatorDialog";
import { MultiSelectFilter } from "../Filters/MultiSelectFilter";
import { formatSlugToTitle } from "../../helpers/stringHelper";

export const CreatorTable = () => {
  const { success } = useNotiStack();
  const [, { openLoader, closeLoader }] = useStore();
  const [openProfileDetailsDialog, setOpenProfileDetailsDialog] = useState(false);
  const [userId, setUserId] = useState(null);
  const [openDeleteAccountConfirmationDialog, setOpenDeleteAccountConfirmationDialog] = useState(false);

  const [openSuspendCreatorDialog, setOpenSuspendCreatorDialog] = useState({
    open: false,
    creator: null,
    mode: "suspension-form",
  });

  const [keyword, setKeyword] = useState("");
  const [tableState, setTableState] = useState({
    filter: {
      search: "",
      status: [ACTIVE, DELETED],
      "roles.name": [ROLES.CREATOR_ROLE, ROLES.PENDING_CREATOR_ROLE],
    },
    sort: ["-created_at"],
    include: ["details.profilePicture", "latestSuspension"],
    pageSize: 10,
    page: 1,
  });

  const {
    data: users,
    isFetching,
    refetch,
  } = useQuery(createUsersQueryOptions(tableState, { placeholderData: keepPreviousData }));

  const handleOpenProfileDetailsDialog = (id) => {
    setUserId(id);
    setOpenProfileDetailsDialog(true);
  };

  const handleOpenDeleteDialog = (id) => {
    setUserId(id);
    setOpenDeleteAccountConfirmationDialog(true);
  };

  const handleCloseProfileDetailsDialog = () => {
    setUserId(null);
    setOpenProfileDetailsDialog(false);
  };

  const handleDeleteAccount = async () => {
    openLoader();
    const response = await deleteAccount({ id: userId });
    if (response.ok) {
      success("Account has been deleted!");
      setOpenDeleteAccountConfirmationDialog(false);
      refetch();
    }
    closeLoader();
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack direction={"row"} alignItems={"center"} gap={1.5} mt={0.5}>
            <GenericAvatar
              location={row?.details?.profile_picture?.location}
              avatarId={row?.id}
              size={"42px"}
              backgroundColor={"background.default"}
            />
            <Typography fontWeight={700}>{row?.name}</Typography>
          </Stack>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "profession",
      headerName: "Profession",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => row?.details?.profession,
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "created_at",
      headerName: "Date",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "referral_code",
      headerName: "Referral Code",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) =>
        row?.details?.meta?.reffered_by ?? (
          <Typography mt={1.7} color="grey.light">
            N/A
          </Typography>
        ),
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography mt={1.7} fontWeight={700} textTransform={"capitalize"} color={STATUS_COLORS[row?.status]}>
            {formatSlugToTitle(row?.status)}
          </Typography>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "details",
      headerName: "Details",
      minWidth: 140,
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Button variant="contained" fullWidth onClick={() => handleOpenProfileDetailsDialog(row?.id)}>
            Details
          </Button>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 290,
      flex: 2,
      renderCell: ({ row }) => {
        const isSuspended = row?.latest_suspension?.is_suspended == 1;

        return (
          <Stack direction={"row"} mt={1} gap={1}>
            <Button fullWidth variant="outlined" color="error" onClick={() => handleOpenDeleteDialog(row?.id)}>
              Delete
            </Button>

            {!isSuspended && (
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={() => setOpenSuspendCreatorDialog({ open: true, creator: row, mode: "suspension-form" })}
              >
                Suspend
              </Button>
            )}

            {isSuspended && (
              <Button
                fullWidth
                variant="contained"
                color="success"
                onClick={() => setOpenSuspendCreatorDialog({ open: true, creator: row, mode: "lift-suspension-action" })}
              >
                Lift suspension
              </Button>
            )}
          </Stack>
        );
      },
      ...GENERIC_COLUMN_PROPS,
    },
  ];

  const [,] = useDebounce(
    () => {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          search: keyword,
        },
        page: 1,
      }));
    },
    1000,
    [keyword]
  );

  const userStatusWithLabel = [
    ...Object.values(USER_STATUS).map((status) => ({
      value: status,
      label: status
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    })),
  ];

  const handleFilterStatusChange = (newValues) => {
    setTableState((prevState) => ({
      ...prevState,
      filter: {
        ...prevState.filter,
        status: newValues,
      },
    }));
  };

  return (
    <Fragment>
      <Card>
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: { xxs: "column", mds: "row" },
              gap: 1,
              mb: 1,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary">
                Creator List
              </Typography>
              <Typography fontWeight={500}>View and manage all approved WannaCollab creators.</Typography>
            </Box>
            <Box width={{ xxs: "100%", mds: "350px" }}>
              <OutlinedInput
                fullWidth
                placeholder="Search by name or email"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <MagnifyingGlass />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Box>
          <MultiSelectFilter
            value={tableState.filter.status}
            onChange={handleFilterStatusChange}
            options={userStatusWithLabel}
            label="Status"
          />
        </Box>

        {/* MOBILE VERSION */}
        <Box sx={{ display: { xxs: "flex", xsm: "none" }, flexDirection: "column", width: "100%" }}>
          <CreatorCardListMobile
            creators={users}
            setTableState={setTableState}
            handleOpenProfileDetailsDialog={handleOpenProfileDetailsDialog}
          />
        </Box>

        {/* TABLE */}
        <Box height={"635px"} sx={{ display: { xxs: "none", xsm: "block" }, overflowX: "auto" }}>
          <CustomDataGrid
            columns={columns}
            pageSizeOptions={PAGE_OPTIONS}
            rows={users?.data ?? []}
            rowCount={users?.total ?? 0}
            paginationMode="server"
            paginationModel={{
              pageSize: users?.per_page ?? 10,
              page: typeof users?.current_page === "number" ? users.current_page - 1 : 0,
            }}
            onPaginationModelChange={({ page, pageSize }) => {
              setTableState((prev) => ({
                ...prev,
                page: pageSize !== prev.pageSize ? 1 : page + 1,
                pageSize,
              }));
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            checkboxSelection={false}
            loading={!users || isFetching}
            slotProps={{
              loadingOverlay: {
                variant: "circular-progress",
              },
            }}
            slots={{
              noResultsOverlay: CustomNoResultsOverlay,
              noRowsOverlay: CustomNoResultsOverlay,
            }}
          />
        </Box>
      </Card>
      <ProfileDetails
        isActionsHidden={true}
        openDialog={openProfileDetailsDialog}
        userId={userId}
        refetchCreatorList={() => refetch()}
        handleClose={handleCloseProfileDetailsDialog}
      />
      <DeleteAccountDialog
        openDeleteAccountConfirmationDialog={openDeleteAccountConfirmationDialog}
        setOpenDeleteAccountConfirmationDialog={setOpenDeleteAccountConfirmationDialog}
        handleDeleteAccount={() => handleDeleteAccount()}
      />
      <SuspendCreatorDialog
        creator={openSuspendCreatorDialog?.creator}
        mode={openSuspendCreatorDialog?.mode}
        setMode={(mode) => setOpenSuspendCreatorDialog((prev) => ({ ...prev, mode }))}
        open={openSuspendCreatorDialog?.open}
        setOpen={(open) => setOpenSuspendCreatorDialog((prev) => ({ ...prev, open }))}
        onSuccess={() => {
          refetch();
        }}
      />
    </Fragment>
  );
};

const CreatorCardListMobile = ({ creators, setTableState, handleOpenProfileDetailsDialog }) => {
  const creatorList = useMemo(() => creators?.data || [], [creators]);

  if (!creatorList.length) {
    return (
      <Box py={2} textAlign="center">
        <CustomNoResultsOverlay />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          height: "450px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {creatorList.map((creator, index) => (
          <Box key={`creator-list-card-${index}`}>
            <CreatorCardMobile creator={creator} handleOpenProfileDetailsDialog={handleOpenProfileDetailsDialog} />
            {index !== creatorList.length - 1 && <Divider sx={{ borderColor: "borders.main" }} />}
          </Box>
        ))}
      </Box>

      <Box>
        <TablePagination
          labelRowsPerPage="Per page:"
          component="div"
          count={creators.total}
          page={creators.current_page - 1}
          rowsPerPageOptions={PAGE_OPTIONS}
          onPageChange={(event, newPage) =>
            setTableState((prev) => ({
              ...prev,
              page: newPage + 1,
            }))
          }
          rowsPerPage={creators.per_page}
          onRowsPerPageChange={(event) =>
            setTableState((prev) => ({
              ...prev,
              pageSize: parseInt(event.target.value, 10),
              page: 1,
            }))
          }
        />
      </Box>
    </Box>
  );
};

const CreatorCardMobile = ({ creator, handleOpenProfileDetailsDialog }) => {
  return (
    <Box border={"0px green solid"} sx={{ padding: "16px" }}>
      <Grid container>
        <Grid item size={4}>
          <GenericAvatar
            location={creator?.details?.profile_picture?.location}
            size={"92px"}
            backgroundColor={"background.default"}
          />
        </Grid>
        <Grid item size={8}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography fontWeight={700} noWrap width={{ xs: 140, md: "100%" }}>
              {creator?.name}
            </Typography>
            <Typography color="text.light">{formatDate(new Date(creator?.created_at), DAY_ONLY_FORMAT)}</Typography>
          </Stack>
          <Stack>
            <Typography noWrap>{creator?.details?.profession ?? "None"}</Typography>
            <Typography fontWeight={700} textTransform={"capitalize"} color={STATUS_COLORS[creator?.status]}>
              {creator?.status}
            </Typography>
            <Button variant="contained" sx={{ mt: 1 }} onClick={() => handleOpenProfileDetailsDialog(creator?.id)}>
              Details
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
